import { useActiveSchedule } from '@griply/hooks';
import { FrequencyUnit, ScheduleEntryState, Task } from '@griply/types';
import {
  formatDateKey,
  getDueTaskPlanning,
  getScheduleByDate,
  getScheduleFrequency,
  getTaskPlanning,
  getTimeframeSchedules,
  isBetween,
  weekdaysNumberMap,
} from '@griply/utils';
import {
  addMonths,
  addWeeks,
  addYears,
  eachMonthOfInterval,
  eachWeekOfInterval,
  eachYearOfInterval,
  endOfMonth,
  endOfWeek,
  endOfYear,
  isBefore,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subDays,
} from 'date-fns';
import { useMemo } from 'react';
import { useToday } from 'shared/contexts/today';

import { useData } from '../../use-data';
import { DayEntry, UpcomingPeriod, UpcomingPeriodTypes } from './types';

export const useUpcomingPeriods = (task: Task): UpcomingPeriod[] => {
  const today = useToday();
  const { weekStartsOn } = useData();
  const activeSchedule = useActiveSchedule(task.schedules, today);

  const unit = useMemo(() => {
    if (!activeSchedule) {
      return FrequencyUnit.Week;
    }

    return getScheduleFrequency(activeSchedule);
  }, [activeSchedule]);

  const dateRange = useMemo(() => {
    const dateFnsOptions = { weekStartsOn: weekdaysNumberMap[weekStartsOn] };
    const startDate =
      unit === FrequencyUnit.Month
        ? startOfMonth(today)
        : unit === FrequencyUnit.Year
          ? startOfYear(today)
          : startOfWeek(today, dateFnsOptions);

    const endDate =
      unit === FrequencyUnit.Month
        ? endOfMonth(addMonths(startDate, 6))
        : unit === FrequencyUnit.Year
          ? endOfYear(addYears(startDate, 2))
          : endOfWeek(addWeeks(startDate, 6), dateFnsOptions);

    return { startDate, endDate };
  }, [today, unit, weekStartsOn]);

  const planning = useMemo(
    () =>
      getTaskPlanning(task, {
        ...dateRange,
        today,
        weekStartsOn,
      }),
    [dateRange, task, today, weekStartsOn],
  );

  return useMemo(() => {
    if (!task.schedules?.length) {
      return [];
    }
    const relevantDates = new Map<string, DayEntry>();

    // add planned occurrences
    planning.forEach((plannedOccurrence) => {
      const dateSchedule = task.schedules?.length
        ? getScheduleByDate(task.schedules, {
            date: plannedOccurrence.date,
            today,
          })
        : undefined;
      const plannedOccurrenceInSchedule =
        dateSchedule?.plannedOccurrences?.find(
          ({ id }) => id === plannedOccurrence.id,
        );
      relevantDates.set(formatDateKey(plannedOccurrence.date), {
        ...plannedOccurrence,
        isUnplannedEntry: false,
        isGeneratedFrequencyEntry:
          !!dateSchedule?.frequency && !plannedOccurrenceInSchedule,
      });
    });

    // add actual entries that are not in planning
    const dateFnsOptions = { weekStartsOn: weekdaysNumberMap[weekStartsOn] };
    const skippedPeriodKeys = new Set<string>();
    const timeframeSchedules = getTimeframeSchedules(task.schedules, dateRange);
    timeframeSchedules.forEach((schedule) => {
      schedule.entries.forEach((entry) => {
        if (!isBetween(entry.date, dateRange.startDate, dateRange.endDate)) {
          return;
        }

        if (entry.hidden && entry.state === ScheduleEntryState.Skip) {
          const periodStart =
            unit === FrequencyUnit.Month
              ? startOfMonth(entry.date)
              : unit === FrequencyUnit.Year
                ? startOfYear(entry.date)
                : startOfWeek(entry.date, dateFnsOptions);
          skippedPeriodKeys.add(formatDateKey(periodStart));
          return;
        }

        if (
          !entry.hidden &&
          ((entry.state === ScheduleEntryState.Complete && !!entry.value) ||
            entry.state === ScheduleEntryState.Failure ||
            entry.state === ScheduleEntryState.Skip)
        ) {
          const key = formatDateKey(entry.date);
          const existing = relevantDates.get(key);
          if (existing?.isGeneratedFrequencyEntry) {
            existing.state = entry.state;
            return;
          }
          relevantDates.set(key, {
            id: entry.id,
            date: entry.date,
            timeslot: null,
            state: entry.state,
            hidden: !!entry.hidden,
            isGeneratedFrequencyEntry: false,
            isUnplannedEntry: true,
          });
        }
      });
    });

    const sortedDates = Array.from(relevantDates.values()).sort(
      (a, b) => a.date.getTime() - b.date.getTime(),
    );

    const groups: UpcomingPeriod[] = [];
    const interval = { start: dateRange.startDate, end: dateRange.endDate };
    const relevantIntervals =
      unit === FrequencyUnit.Month
        ? eachMonthOfInterval(interval)
        : unit === FrequencyUnit.Year
          ? eachYearOfInterval(interval)
          : eachWeekOfInterval(interval, dateFnsOptions);

    relevantIntervals.forEach((periodStart) => {
      const periodEnd =
        unit === FrequencyUnit.Month
          ? endOfMonth(periodStart)
          : unit === FrequencyUnit.Year
            ? endOfYear(periodStart)
            : endOfWeek(periodStart, dateFnsOptions);

      const daysInPeriod = sortedDates.filter(({ date }) =>
        isBetween(date, periodStart, periodEnd),
      );

      const mappedDays = daysInPeriod.map((entry) => ({
        date: entry.date,
        entry,
      }));

      const activeScheduleForPeriod = task.schedules?.length
        ? getScheduleByDate(task.schedules, { date: periodStart, today })
        : undefined;

      const hasFrequencyTarget = !!activeScheduleForPeriod?.frequency?.count;

      const isFrequencyPeriodSkipped =
        hasFrequencyTarget && skippedPeriodKeys.has(formatDateKey(periodStart));

      if (hasFrequencyTarget && !mappedDays.length) {
        if (!isFrequencyPeriodSkipped) {
          mappedDays.push({
            date: periodStart,
            entry: {
              id: `generated-${periodStart.getTime()}`,
              date: periodStart,
              timeslot: null,
              hidden: false,
              isGeneratedFrequencyEntry: true,
              isUnplannedEntry: false,
            },
          });
        }

        if (isFrequencyPeriodSkipped) {
          const hiddenSkipEntry = activeScheduleForPeriod?.entries.find(
            (entry) =>
              entry.hidden &&
              entry.state === ScheduleEntryState.Skip &&
              isBetween(entry.date, periodStart, periodEnd),
          );
          if (hiddenSkipEntry) {
            mappedDays.push({
              date: hiddenSkipEntry.date,
              entry: {
                id: hiddenSkipEntry.id,
                date: hiddenSkipEntry.date,
                timeslot: null,
                state: ScheduleEntryState.Skip,
                hidden: true,
                isGeneratedFrequencyEntry: true,
                isUnplannedEntry: false,
              },
            });
          }
        }
      }

      groups.push({
        date: periodStart,
        days: mappedDays,
        type: unit,
      });
    });

    const overduePlanning = getDueTaskPlanning(task, {
      date: today,
      today,
      weekStartsOn,
      uncompletedOnly: true,
    });

    const overdueEntries: DayEntry[] = overduePlanning
      .filter((entry) => isBefore(entry.date, dateRange.startDate))
      .map((plannedOccurrence) => ({
        ...plannedOccurrence,
        isGeneratedFrequencyEntry: false,
        isUnplannedEntry: false,
        hidden: false,
      }));

    if (overdueEntries.length) {
      groups.unshift({
        date: subDays(dateRange.startDate, 1),
        days: overdueEntries.map((entry) => ({ date: entry.date, entry })),
        type: UpcomingPeriodTypes.Overdue,
      });
    }

    return groups;
  }, [dateRange, planning, task, today, unit, weekStartsOn]);
};
